import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import { useLogger } from '~Hooks';
import { StageVideo } from '../../styles';

interface Props {
  src: string;
  /** Holds the video on its current frame - set while the menu sheet is open. */
  paused?: boolean;
}

/**
 * The stage background as an endlessly looping, muted, control-less video.
 * Muted + playsInline is what lets iOS/Android web views autoplay it without
 * a tap. `loop` handles the restart; the handlers below are a safety net for
 * web views that stop it anyway (end reached, paused by the OS on
 * backgrounding, autoplay rejected on first load).
 */
const BackgroundVideo: FC<Props> = ({ src, paused = false }) => {
  const logger = useLogger('epos-background-video');
  const videoRef = useRef<HTMLVideoElement>(null);
  /** Read by the resume safety nets so they never restart a deliberately paused video. */
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  /** Starts (or restarts) playback, logging if the browser refuses. No-op while paused. */
  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video || pausedRef.current) return;
    video.muted = true;
    const promise = video.play();
    if (promise) {
      promise.catch((err: unknown) => {
        logger.warn('EPOS background video could not start playing', {
          src,
          reason: String(err),
        });
      });
    }
  }, [src]);

  /** A new src needs an explicit kick - autoPlay only fires on the first load in some web views. */
  useEffect(() => {
    play();
  }, [src, play]);

  /** Pause on the current frame while the menu is open, pick up from there when it closes. */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    logger.info(paused ? 'Pausing EPOS background video' : 'Resuming EPOS background video', {
      src,
    });
    if (paused) {
      video.pause();
    } else {
      play();
    }
  }, [paused]);

  /** Coming back to the foreground can leave the video paused - resume it. */
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) play();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [play]);

  /** `loop` should prevent this, but restart manually if a web view ignores it. */
  const handleEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    play();
  }, [play]);

  const handleError = useCallback(() => {
    logger.error('EPOS background video failed to load', {
      src,
      mediaErrorCode: videoRef.current?.error?.code,
    });
  }, [src]);

  return (
    <StageVideo
      ref={videoRef}
      src={src}
      autoPlay
      muted
      loop
      playsInline
      disablePictureInPicture
      controls={false}
      preload="auto"
      aria-hidden
      tabIndex={-1}
      onEnded={handleEnded}
      onError={handleError}
    />
  );
};

export default memo(BackgroundVideo);
